import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import withdrawalService from '../../api/services/withdrawal.service';
import { CheckCircleIcon, XCircleIcon, BanknotesIcon } from '@heroicons/react/24/outline';

interface WithdrawalDetailModalProps {
  isOpen: boolean;
  withdrawalId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-900 text-right break-all">
      {value}
    </span>
  </div>
);

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    pending: 'bg-yellow-50 text-yellow-700',
    accepted: 'bg-blue-50 text-blue-700',
    paid: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const WithdrawalDetailModal: React.FC<WithdrawalDetailModalProps> = ({
  isOpen,
  withdrawalId,
  onClose,
  onChanged,
}) => {
  const queryClient = useQueryClient();
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['withdrawal-detail', withdrawalId],
    queryFn: () => withdrawalService.getOne(withdrawalId as string),
    enabled: isOpen && !!withdrawalId,
    staleTime: 30 * 1000,
  });

  const handle = async (action: 'accept' | 'paid' | 'reject') => {
    if (!data) return;
    setErr(null);
    setBusy(true);
    try {
      if (action === 'accept') await withdrawalService.accept(data.id);
      if (action === 'paid') await withdrawalService.markPaid(data.id);
      if (action === 'reject') {
        if (!rejectReason.trim()) {
          setErr('Reason is required');
          setBusy(false);
          return;
        }
        await withdrawalService.reject(data.id, rejectReason.trim());
      }
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawal-stats'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawal-detail', data.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onChanged();
      setRejectMode(false);
      setRejectReason('');
      refetch();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Withdrawal Details" size="md">
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 py-4">
          {error instanceof Error ? error.message : 'Failed to load withdrawal'}
        </p>
      )}

      {data && (
        <div className="space-y-4">
          {err && (
            <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-primary-700 uppercase tracking-wide">
                Amount Requested
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {data.currency} {data.amount.toLocaleString()}
              </p>
            </div>
            <span
              className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${statusBadge(
                data.status
              )}`}
            >
              {data.status}
            </span>
          </div>

          {/* User */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              User
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Name" value={data.userName} />
              <Row label="Username" value={`@${data.userUsername || '—'}`} />
              <Row label="Telegram ID" value={data.telegramId} />
              <Row label="Phone" value={data.userPhone || '—'} />
              <Row
                label="Balance at Request"
                value={`${data.currency} ${data.balanceAtRequest.toLocaleString()}`}
              />
              {data.newBalance !== null && (
                <Row
                  label="Balance After Paid"
                  value={`${data.currency} ${data.newBalance.toLocaleString()}`}
                />
              )}
            </div>
          </div>

          {/* Bank / account */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Bank & Account
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Bank" value={`${data.bankName} (${data.bankShortCode})`} />
              <Row label="Account Number" value={data.accountNumber} />
              <Row label="Account Name" value={data.accountName} />
              {data.note && <Row label="Note" value={data.note} />}
              {data.rejectReason && (
                <Row
                  label="Reject Reason"
                  value={<span className="text-red-600">{data.rejectReason}</span>}
                />
              )}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Timeline
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row
                label="Requested"
                value={new Date(data.createdAt).toLocaleString()}
              />
              {data.acceptedAt && (
                <Row
                  label="Accepted"
                  value={new Date(data.acceptedAt).toLocaleString()}
                />
              )}
              {data.paidAt && (
                <Row
                  label="Paid"
                  value={new Date(data.paidAt).toLocaleString()}
                />
              )}
              {data.rejectedAt && (
                <Row
                  label="Rejected"
                  value={new Date(data.rejectedAt).toLocaleString()}
                />
              )}
            </div>
          </div>

          {/* Reject reason form */}
          {rejectMode && (
            <div className="border border-red-200 bg-red-50/60 rounded-lg p-3 space-y-2">
              <label className="block text-xs font-medium text-red-700">
                Rejection Reason
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={2}
                placeholder="Why is this withdrawal being rejected?"
                className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setRejectMode(false);
                    setRejectReason('');
                  }}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handle('reject')}
                  disabled={busy}
                >
                  {busy ? 'Rejecting...' : 'Confirm Reject'}
                </Button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!rejectMode && (
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
              {data.status === 'pending' && (
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setRejectMode(true)}
                    disabled={busy}
                  >
                    <XCircleIcon className="w-4 h-4 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handle('accept')}
                    disabled={busy}
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                    {busy ? 'Accepting...' : 'Accept'}
                  </Button>
                </>
              )}

              {data.status === 'accepted' && (
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setRejectMode(true)}
                    disabled={busy}
                  >
                    <XCircleIcon className="w-4 h-4 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handle('paid')}
                    disabled={busy}
                  >
                    <BanknotesIcon className="w-4 h-4 mr-1.5" />
                    {busy ? 'Processing...' : 'Mark as Paid'}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default WithdrawalDetailModal;